import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  User, Mail, Briefcase, Calendar, ArrowLeft, CheckCircle, Clock,
  ClipboardList, FolderOpen, BookOpen, Heart, Lightbulb, Palette,
  TrendingUp, Users, FileText, Award, Target
} from 'lucide-react';
import { formatDateSafe } from '../utils/textUtils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIMENSION_CONFIG = {
  cness_tasks: { name: 'CNESS Tasks', icon: ClipboardList, color: '#7077FE' },
  self_care: { name: 'Self Care', icon: Heart, color: '#10B981' },
  rnd: { name: 'R&D', icon: Lightbulb, color: '#6366F1' },
  personal_growth: { name: 'Personal Growth', icon: TrendingUp, color: '#F59E0B' },
  creative: { name: 'Creative', icon: Palette, color: '#EC4899' }
};

const UserProfile = ({ currentUser }) => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profileUser, setProfileUser] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [sops, setSops] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (userId) {
      fetchProfileData();
    }
  }, [userId]);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch user details
      const userRes = await axios.get(`${API_URL}/users/${userId}`, { headers });
      setProfileUser(userRes.data);

      // Fetch tasks assigned to this user
      const tasksRes = await axios.get(`${API_URL}/tasks?page=1&page_size=100`, { headers });
      const userTasks = (tasksRes.data.tasks || []).filter(t => 
        t.assigned_to?.includes(userId) || t.assigned_by === userId
      );
      setTasks(userTasks);

      // Fetch SOPs assigned to this user
      try {
        const sopsRes = await axios.get(`${API_URL}/sops`, { headers });
        const userSops = (sopsRes.data || []).filter(s => s.assigned_to === userId);
        setSops(userSops);
      } catch (e) {
        console.error('Failed to fetch SOPs');
      }

      // Fetch projects
      try {
        const projectsRes = await axios.get(`${API_URL}/projects`, { headers });
        const userProjects = (projectsRes.data || []).filter(p => 
          p.team_head_ids?.includes(userId) || 
          p.member_ids?.includes(userId) ||
          p.created_by === userId
        );
        setProjects(userProjects);
      } catch (e) {
        console.error('Failed to fetch projects');
      }

    } catch (error) {
      console.error('Failed to fetch profile data:', error);
      toast.error('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      superadmin: '#DC2626',
      ceo: '#7C3AED',
      manager: '#2563EB',
      team_lead: '#059669',
      team_member: '#6B7280',
      educator: '#D97706'
    };
    return colors[role] || '#6B7280';
  };

  const getRoleName = (role) => {
    const names = {
      superadmin: 'Super Admin',
      ceo: 'CEO',
      manager: 'Manager',
      team_lead: 'Team Lead',
      team_member: 'Team Member',
      educator: 'Educator'
    };
    return names[role] || role;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getDimensionStats = () => {
    const stats = {};
    Object.keys(DIMENSION_CONFIG).forEach(dim => {
      stats[dim] = tasks.filter(t => 
        Array.isArray(t.dimension) ? t.dimension.includes(dim) : t.dimension === dim
      ).length;
    });
    return stats;
  };

  const getTaskStats = () => {
    const assigned = tasks.filter(t => t.assigned_to?.includes(userId));
    const created = tasks.filter(t => t.assigned_by === userId);
    const completed = assigned.filter(t => t.status === 'completed');
    const pending = assigned.filter(t => t.status === 'pending');
    const inProgress = assigned.filter(t => t.status === 'in_progress');
    
    return { assigned: assigned.length, created: created.length, completed: completed.length, pending: pending.length, inProgress: inProgress.length };
  };

  if (loading) {
    return (
      <div className="content-area" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!profileUser) {
    return (
      <div className="content-area">
        <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
          <User size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
          <h3>User not found</h3>
          <button onClick={() => navigate(-1)} className="gradient-btn" style={{ marginTop: '16px' }}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const dimensionStats = getDimensionStats();
  const taskStats = getTaskStats();
  const isOwnProfile = currentUser?.id === userId;

  return (
    <div className="content-area">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          background: 'var(--bg-secondary)',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          marginBottom: '24px',
          color: 'var(--text-primary)',
          fontSize: '14px'
        }}
      >
        <ArrowLeft size={18} />
        Back
      </button>

      {/* Profile Header Card */}
      <div className="card" style={{ padding: '32px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Avatar */}
          <div style={{
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: profileUser.avatar_url 
              ? `url(${profileUser.avatar_url}) center/cover` 
              : `linear-gradient(135deg, ${getRoleColor(profileUser.role)}, ${getRoleColor(profileUser.role)}99)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '36px',
            fontWeight: '700',
            color: 'white',
            flexShrink: 0,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
          }}>
            {!profileUser.avatar_url && getInitials(profileUser.full_name)}
          </div>

          {/* User Info */}
          <div style={{ flex: 1, minWidth: '250px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>
                {profileUser.full_name || 'Unknown User'}
              </h1>
              <span style={{
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                background: `${getRoleColor(profileUser.role)}15`,
                color: getRoleColor(profileUser.role)
              }}>
                {getRoleName(profileUser.role)}
              </span>
              {isOwnProfile && (
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '600',
                  background: '#D1FAE5',
                  color: '#059669'
                }}>
                  You
                </span>
              )}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)', fontSize: '14px' }}>
                <Mail size={16} />
                {profileUser.email}
              </div>
              {profileUser.created_at && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)', fontSize: '14px' }}>
                  <Calendar size={16} />
                  Joined {formatDateSafe(profileUser.created_at)}
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div style={{ display: 'flex', gap: '24px', marginTop: '20px', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>{taskStats.assigned}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Tasks Assigned</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#10B981' }}>{taskStats.completed}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Completed</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>{projects.length}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Projects</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>{sops.length}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>SOPs</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid var(--border-color)' }}>
        {['overview', 'tasks', 'projects', 'sops'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            data-testid={`profile-tab-${tab}`}
            style={{
              padding: '12px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: activeTab === tab ? '#7077FE' : 'var(--text-tertiary)',
              borderBottom: activeTab === tab ? '3px solid #7077FE' : '3px solid transparent',
              marginBottom: '-1px',
              textTransform: 'capitalize'
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          {/* Dimensions Working On */}
          <div className="card" style={{ padding: '24px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Target size={20} style={{ color: '#7077FE' }} />
              Dimensions Working On
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {Object.entries(DIMENSION_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                const count = dimensionStats[key];
                return (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: `${config.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon size={18} style={{ color: config.color }} />
                      </div>
                      <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{config.name}</span>
                    </div>
                    <span style={{ 
                      padding: '4px 12px', 
                      borderRadius: '12px', 
                      background: count > 0 ? `${config.color}15` : 'var(--bg-secondary)',
                      color: count > 0 ? config.color : 'var(--text-tertiary)',
                      fontWeight: '600',
                      fontSize: '13px'
                    }}>
                      {count} tasks
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Task Status Breakdown */}
          <div className="card" style={{ padding: '24px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ClipboardList size={20} style={{ color: '#7077FE' }} />
              Task Status
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Clock size={18} style={{ color: '#F59E0B' }} />
                  <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>Pending</span>
                </div>
                <span style={{ fontWeight: '700', color: '#F59E0B' }}>{taskStats.pending}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Clock size={18} style={{ color: '#3B82F6' }} />
                  <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>In Progress</span>
                </div>
                <span style={{ fontWeight: '700', color: '#3B82F6' }}>{taskStats.inProgress}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <CheckCircle size={18} style={{ color: '#10B981' }} />
                  <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>Completed</span>
                </div>
                <span style={{ fontWeight: '700', color: '#10B981' }}>{taskStats.completed}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'tasks' && (
        <div>
          {tasks.length === 0 ? (
            <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
              <ClipboardList size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0' }}>No tasks found</h3>
              <p style={{ margin: 0, color: 'var(--text-tertiary)' }}>This user has no tasks assigned</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {tasks.slice(0, 20).map(task => (
                <div
                  key={task.id}
                  className="card"
                  style={{ padding: '16px', cursor: 'pointer', borderLeft: `4px solid ${task.status === 'completed' ? '#10B981' : task.status === 'in_progress' ? '#3B82F6' : '#F59E0B'}` }}
                  onClick={() => navigate(`/dashboard/tasks/${task.id}`)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '600' }}>{task.title}</h4>
                      <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                        {task.deadline && <span>Due: {formatDateSafe(task.deadline)}</span>}
                        <span style={{ textTransform: 'capitalize' }}>{task.status?.replace('_', ' ')}</span>
                      </div>
                    </div>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '600',
                      background: task.assigned_to?.includes(userId) ? '#DBEAFE' : '#FEF3C7',
                      color: task.assigned_to?.includes(userId) ? '#1D4ED8' : '#D97706'
                    }}>
                      {task.assigned_to?.includes(userId) ? 'Assigned' : 'Created'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'projects' && (
        <div>
          {projects.length === 0 ? (
            <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
              <FolderOpen size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0' }}>No projects found</h3>
              <p style={{ margin: 0, color: 'var(--text-tertiary)' }}>This user is not part of any projects</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
              {projects.map(project => (
                <div
                  key={project.id}
                  className="card"
                  style={{ padding: '20px', cursor: 'pointer' }}
                  onClick={() => navigate(`/dashboard/projects/${project.id}`)}
                >
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>{project.name}</h4>
                  <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: 'var(--text-secondary)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {project.description}
                  </p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {project.team_head_ids?.includes(userId) && (
                      <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', background: '#DBEAFE', color: '#1D4ED8' }}>Team Head</span>
                    )}
                    {project.member_ids?.includes(userId) && (
                      <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', background: '#D1FAE5', color: '#059669' }}>Member</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'sops' && (
        <div>
          {sops.length === 0 ? (
            <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
              <FileText size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0' }}>No SOPs assigned</h3>
              <p style={{ margin: 0, color: 'var(--text-tertiary)' }}>This user has no SOPs assigned</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {sops.map(sop => (
                <div
                  key={sop.id}
                  className="card"
                  style={{ padding: '16px', cursor: 'pointer' }}
                  onClick={() => navigate(`/dashboard/sops/${sop.id}`)}
                >
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '600' }}>{sop.title}</h4>
                  <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                    Created {formatDateSafe(sop.created_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserProfile;
